/**
 * MissionModal — Full-featured mission management modal for the AI chat tools popover.
 *
 * Follows the StyleIntelligenceModal / SkillsModal pattern:
 *   export useMission() hook  +  export MissionModal component.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { missionsApi } from '@/services/api/agentApi';
import { toast } from 'sonner';
import {
  RefreshCw, Play, Pause, XCircle, CheckCircle2, ChevronDown, ChevronUp,
  Rocket, DollarSign, Clock, AlertTriangle, Plus, Filter, Server, Send,
  Eye, FileText, Zap, TestTube, Activity, Settings2, MessageSquare,
  Layers, ArrowLeft, Crosshair, Loader2, Star,
} from 'lucide-react';
import type {
  Mission, MissionBudget, MissionLog, MissionMetadata, MissionArtifact,
  StandupReport, MissionDetailTab,
} from '@/types/mission';
import {
  MISSION_PHASES, missionStatusColor, missionBudgetPercent,
  missionBudgetBarColor, getPhaseIndex, isMissionPastPhase,
  isMissionAtPhase, relativeTime,
} from '@/types/mission';

// ── Hook ────────────────────────────────────────────────────────────

export interface UseMissionReturn {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

export function useMission(): UseMissionReturn {
  const [isOpen, setIsOpen] = useState(false);
  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);
  return { isOpen, openModal, closeModal };
}

// ── Sub-components ──────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const isActive = ['discovery', 'planning', 'executing', 'testing'].includes(status);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${missionStatusColor(status)}`}>
      {isActive && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
      {status === 'failed' && <AlertTriangle className="h-2.5 w-2.5" />}
      {status}
    </span>
  );
};

const BudgetBar: React.FC<{ budget: MissionBudget }> = ({ budget }) => {
  const pct = missionBudgetPercent(budget);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${missionBudgetBarColor(pct)}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        ${budget.cost_used_usd.toFixed(2)} / ${budget.cost_allocated_usd.toFixed(2)}
      </span>
    </div>
  );
};

const PhaseTimeline: React.FC<{ mission: Mission }> = ({ mission }) => (
  <div className="flex items-center gap-1">
    {MISSION_PHASES.map((phase, i) => {
      const past = isMissionPastPhase(mission, phase);
      const current = isMissionAtPhase(mission, phase);
      const dotClass = past
        ? 'bg-green-500'
        : current
          ? 'bg-primary ring-2 ring-primary/30 animate-pulse'
          : 'bg-muted-foreground/30';
      return (
        <React.Fragment key={phase}>
          <div className="flex flex-col items-center" title={phase}>
            <div className={`w-2.5 h-2.5 rounded-full ${dotClass}`} />
            <span className={`text-[9px] mt-0.5 ${current ? 'text-primary font-medium' : 'text-muted-foreground/60'}`}>
              {phase.slice(0, 4)}
            </span>
          </div>
          {i < MISSION_PHASES.length - 1 && (
            <div className={`flex-1 h-px min-w-[8px] ${past ? 'bg-green-500' : 'bg-muted-foreground/20'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── Detail Tabs ─────────────────────────────────────────────────────

const OverviewTab: React.FC<{ mission: Mission; standup: StandupReport | null; logs: MissionLog[] }> = ({ mission, standup, logs }) => {
  const pct = missionBudgetPercent(mission.budget);
  const instances = [...new Set(logs.map(l => l.instance_id).filter(Boolean))];
  const phaseCosts = logs.reduce<Record<string, number>>((acc, l) => {
    if (l.cost_usd > 0) acc[l.phase] = (acc[l.phase] || 0) + l.cost_usd;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><DollarSign className="h-3 w-3" />Budget</div>
          <div className="text-sm font-medium">{pct}% used</div>
          <div className="text-xs text-muted-foreground">{mission.budget.tokens_used.toLocaleString()} / {mission.budget.tokens_allocated.toLocaleString()} tokens</div>
        </div>
        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><Rocket className="h-3 w-3" />Artifacts</div>
          <div className="text-sm font-medium">{mission.artifacts.length}</div>
          <div className="text-xs text-muted-foreground">{mission.artifacts.map(a => a.type).filter((v, i, a) => a.indexOf(v) === i).join(', ') || 'None yet'}</div>
        </div>
        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><Clock className="h-3 w-3" />Heartbeat</div>
          <div className="text-sm font-medium">{relativeTime(mission.last_heartbeat_at)}</div>
          <div className="text-xs text-muted-foreground">Created {new Date(mission.created_at).toLocaleDateString()}</div>
        </div>
        <div className="bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><AlertTriangle className="h-3 w-3" />Escalations</div>
          <div className="text-sm font-medium">{mission.escalation.total_escalations}</div>
          <div className="text-xs text-muted-foreground">{mission.escalation.consecutive_failures > 0 ? `${mission.escalation.consecutive_failures} consecutive failures` : 'No current issues'}</div>
        </div>
      </div>

      {instances.length > 0 && (
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Server className="h-3 w-3" />Running on {instances.length} instance{instances.length !== 1 ? 's' : ''}: {instances.join(', ')}
        </div>
      )}

      {Object.keys(phaseCosts).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(phaseCosts).map(([phase, cost]) => (
            <span key={phase} className="text-[10px] px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground">{phase}: ${cost.toFixed(3)}</span>
          ))}
        </div>
      )}

      {standup && (
        <div className="bg-muted/20 rounded-lg p-3">
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Latest Standup</h4>
          <pre className="text-xs whitespace-pre-wrap font-sans">{standup.formatted}</pre>
        </div>
      )}

      {mission.escalation.stuck_reason && (
        <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-xs font-medium text-red-600 mb-1"><AlertTriangle className="h-3 w-3" /> Stuck</div>
          <p className="text-xs text-red-600/80">{mission.escalation.stuck_reason}</p>
        </div>
      )}
    </div>
  );
};

const PlanTab: React.FC<{ mission: Mission; onAction: (id: string, action: string) => void }> = ({ mission, onAction }) => {
  if (!mission.plan) {
    return <div className="text-center py-8 text-muted-foreground text-sm">No plan generated yet. The mission is still in the <span className="font-medium">{mission.phase}</span> phase.</div>;
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-medium text-muted-foreground">Plan v{mission.plan.version}</h4>
        <div className="flex items-center gap-2">
          {mission.plan.user_approved ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-600 border border-green-500/20">Approved</span>
          ) : (
            <>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-600 border border-yellow-500/20">Pending Approval</span>
              {mission.status === 'planning' && (
                <button onClick={() => onAction(mission.mission_id, 'approve-plan')} className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20 border border-indigo-500/20">
                  <CheckCircle2 className="h-3 w-3" />Approve
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {mission.plan.phases.map((p, i) => (
        <div key={i} className="bg-muted/20 rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium capitalize">{p.phase}</span>
            {p.estimated_tokens > 0 && <span className="text-[10px] text-muted-foreground">~{p.estimated_tokens.toLocaleString()} tokens</span>}
          </div>
          <p className="text-xs text-muted-foreground mb-2">{p.description}</p>
          {p.tasks.length > 0 && (
            <div className="space-y-1 ml-2">
              {p.tasks.map((t, j) => (
                <div key={j} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <span className="text-muted-foreground/40 select-none">{j + 1}.</span><span>{t}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {mission.plan.self_critique && (
        <div className="bg-muted/10 rounded-lg p-3 border border-muted/20">
          <h4 className="text-[10px] font-medium text-muted-foreground mb-1">Self-Critique</h4>
          <p className="text-xs text-muted-foreground italic">{mission.plan.self_critique}</p>
        </div>
      )}
    </div>
  );
};

const ArtifactCard: React.FC<{
  artifact: MissionArtifact;
  spec?: { type: string; name: string; specification: string; delegationResult?: string; externalId?: string };
}> = ({ artifact: a, spec }) => {
  const [expanded, setExpanded] = useState(false);
  const schemaDef = a.delegationContext?.response?.schemaDefinition;
  const fields: Array<{ name: string; type: string; required?: boolean; description?: string }> = schemaDef?.constraints?.fields || [];
  const description = schemaDef?.metadata?.description || schemaDef?.description || '';
  const schemaName = schemaDef?.name || '';
  const isSchema = a.type === 'schema';

  return (
    <div className="border border-border rounded-lg bg-background hover:bg-accent/30 transition-colors">
      <div className="flex items-start justify-between p-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isSchema && fields.length > 0 && (
              <span className="flex items-center gap-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded">
                <Layers className="h-3 w-3" />{fields.length} field{fields.length !== 1 ? 's' : ''}
              </span>
            )}
            {!isSchema && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 font-medium uppercase">{a.type}</span>
            )}
            <span className="font-medium text-sm truncate">{a.name}</span>
            {a.externalId ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Created</span>
            ) : (
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">Not Created</span>
            )}
            {expanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
          </div>
          {schemaName && (
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              <span className="font-mono">{schemaName}</span>
              {a.service && <><span>&middot;</span><span>via {a.service}</span></>}
              <span>&middot;</span><span>{relativeTime(a.created_at)}</span>
            </div>
          )}
          {description && <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{description}</p>}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-border px-3 py-2">
          {fields.length > 0 ? (
            <div className="space-y-1">
              {fields.map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-xs py-1 border-b border-border/50 last:border-0">
                  <span className="font-mono font-medium text-foreground w-36 truncate">{f.name}</span>
                  <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono text-[10px] uppercase w-16 text-center">{(f.type || '').toLowerCase()}</span>
                  {f.required && <span className="text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">required</span>}
                  {f.description && <span className="text-muted-foreground truncate flex-1">{f.description}</span>}
                </div>
              ))}
            </div>
          ) : spec?.specification ? (
            <pre className="text-[11px] text-foreground/80 whitespace-pre-wrap font-mono bg-muted/30 rounded p-2 max-h-64 overflow-auto">{spec.specification}</pre>
          ) : a.delegationContext?.request?.intent ? (
            <p className="text-xs text-muted-foreground">{a.delegationContext.request.intent}</p>
          ) : (
            <p className="text-xs text-muted-foreground italic">No specification available</p>
          )}
        </div>
      )}
    </div>
  );
};

const ExecutionTab: React.FC<{ mission: Mission }> = ({ mission }) => {
  const execution = mission.metadata?.execution;
  const executionPhase = mission.plan?.phases.find(p => p.phase === 'execution');
  const tasks = executionPhase?.tasks || [];
  const completedTasks = execution?.completedTasks || [];
  const currentIndex = execution?.currentTaskIndex || 0;
  const artifactSpecs = execution?.artifactSpecs || {};

  return (
    <div className="space-y-4">
      {tasks.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-medium text-muted-foreground">Task Progress</h4>
            <span className="text-[10px] text-muted-foreground">{completedTasks.length}/{tasks.length} tasks</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden mb-3">
            <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${tasks.length > 0 ? (completedTasks.length / tasks.length) * 100 : 0}%` }} />
          </div>
          <div className="space-y-1.5">
            {tasks.map((task, i) => {
              const done = i < currentIndex;
              const active = i === currentIndex && currentIndex < tasks.length;
              return (
                <div key={i} className="flex items-start gap-2 text-xs">
                  {done ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" /> : active ? <div className="w-3.5 h-3.5 rounded-full border-2 border-amber-500 flex-shrink-0 mt-0.5 animate-pulse" /> : <div className="w-3.5 h-3.5 rounded-full border border-muted-foreground/30 flex-shrink-0 mt-0.5" />}
                  <span className={done ? 'text-muted-foreground line-through' : active ? 'text-foreground font-medium' : 'text-muted-foreground/60'}>{task}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {mission.artifacts.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Created Artifacts</h4>
          <div className="space-y-2">
            {mission.artifacts.map((a, i) => {
              const spec = artifactSpecs[a.id] || Object.values(artifactSpecs).find(s => s.name === a.name);
              return <ArtifactCard key={i} artifact={a} spec={spec} />;
            })}
          </div>
        </div>
      )}
      {Object.keys(artifactSpecs).length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Delegation Results</h4>
          <div className="space-y-1.5">
            {Object.entries(artifactSpecs).map(([id, spec]) => (
              <div key={id} className="flex items-center gap-2 text-xs">
                {spec.delegationResult === 'success' ? <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0" /> : <XCircle className="h-3 w-3 text-red-500 flex-shrink-0" />}
                <span className="font-medium">{spec.name}</span>
                <span className="text-muted-foreground">({spec.type})</span>
                {spec.externalId && <span className="text-muted-foreground/50 text-[10px]">{spec.externalId.slice(0, 10)}...</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {mission.artifacts.length === 0 && tasks.length === 0 && (
        <div className="text-center py-8 text-muted-foreground text-sm">Execution has not started yet.</div>
      )}
    </div>
  );
};

const DiscoveryTab: React.FC<{ discovery: MissionMetadata['discovery'] }> = ({ discovery }) => {
  if (!discovery) return <div className="text-center py-8 text-muted-foreground text-sm">No discovery data yet.</div>;
  return (
    <div className="space-y-3">
      {discovery.findings && (
        <div className="bg-purple-500/5 rounded-lg p-3 border border-purple-500/10">
          <h4 className="text-xs font-medium text-purple-600 mb-1">Findings</h4>
          <p className="text-xs text-foreground">{discovery.findings}</p>
        </div>
      )}
      {discovery.requirements && discovery.requirements.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1.5">Requirements</h4>
          <ul className="space-y-1">
            {discovery.requirements.map((r, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs"><CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" /><span>{r}</span></li>
            ))}
          </ul>
        </div>
      )}
      {discovery.existing_resources && discovery.existing_resources.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1.5">Existing Resources</h4>
          <ul className="space-y-1">
            {discovery.existing_resources.map((r, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5"><Server className="h-3 w-3 flex-shrink-0 mt-0.5" /><span>{r}</span></li>
            ))}
          </ul>
        </div>
      )}
      {discovery.gaps && discovery.gaps.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1.5">Gaps</h4>
          <ul className="space-y-1">
            {discovery.gaps.map((g, i) => (
              <li key={i} className="text-xs text-yellow-600 flex items-start gap-1.5"><AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" /><span>{g}</span></li>
            ))}
          </ul>
        </div>
      )}
      {discovery.suggested_artifacts && discovery.suggested_artifacts.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1.5">Suggested Artifacts</h4>
          <div className="space-y-1.5">
            {discovery.suggested_artifacts.map((a, i) => (
              <div key={i} className="bg-muted/20 rounded p-2 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] px-1 py-0.5 rounded bg-primary/10 text-primary uppercase font-medium">{a.type}</span>
                  <span className="font-medium">{a.name}</span>
                </div>
                <p className="text-muted-foreground mt-0.5">{a.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {discovery.iteration !== undefined && (
        <div className="text-[10px] text-muted-foreground">Discovery iteration: {discovery.iteration}{discovery.sufficient ? ' (sufficient)' : ''}</div>
      )}
    </div>
  );
};

const TestsTab: React.FC<{ testing: MissionMetadata['testing'] }> = ({ testing }) => {
  if (!testing?.results?.length) return <div className="text-center py-8 text-muted-foreground text-sm">No test results yet.</div>;
  const passed = testing.results.filter(r => r.passed).length;
  const total = testing.results.length;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium">{passed}/{total} passed</span>
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${passed === total ? 'bg-green-500' : 'bg-yellow-500'}`} style={{ width: `${(passed / total) * 100}%` }} />
        </div>
      </div>
      <div className="space-y-2">
        {testing.results.map((r, i) => (
          <div key={i} className={`rounded-lg p-3 border ${r.passed ? 'bg-green-500/5 border-green-500/10' : 'bg-red-500/5 border-red-500/10'}`}>
            <div className="flex items-center gap-2 mb-1">
              {r.passed ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <XCircle className="h-3.5 w-3.5 text-red-500" />}
              <span className="text-xs font-medium">{r.artifactName}</span>
            </div>
            {r.notes && <p className="text-xs text-muted-foreground ml-5">{r.notes}</p>}
            {r.issues && r.issues.length > 0 && (
              <ul className="ml-5 mt-1 space-y-0.5">
                {r.issues.map((issue, j) => <li key={j} className="text-xs text-red-600">- {issue}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const ActivityTab: React.FC<{ logs: MissionLog[] }> = ({ logs }) => {
  const [phaseFilter, setPhaseFilter] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [llmOnly, setLlmOnly] = useState(false);

  const filtered = logs.filter(l => {
    if (phaseFilter && l.phase !== phaseFilter) return false;
    if (outcomeFilter && l.outcome !== outcomeFilter) return false;
    if (llmOnly && l.tokens_used <= 0) return false;
    return true;
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-3 w-3 text-muted-foreground" />
        <select value={phaseFilter} onChange={e => setPhaseFilter(e.target.value)} className="text-[10px] bg-transparent border border-border rounded px-1.5 py-0.5 text-muted-foreground">
          <option value="">All phases</option>
          {MISSION_PHASES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={outcomeFilter} onChange={e => setOutcomeFilter(e.target.value)} className="text-[10px] bg-transparent border border-border rounded px-1.5 py-0.5 text-muted-foreground">
          <option value="">All outcomes</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
          <option value="escalated">Escalated</option>
          <option value="deferred">Deferred</option>
        </select>
        <button onClick={() => setLlmOnly(p => !p)} className={`text-[10px] px-1.5 py-0.5 rounded border ${llmOnly ? 'bg-primary/10 text-primary border-primary/20' : 'border-border text-muted-foreground'}`}>
          LLM calls only
        </button>
        <span className="text-[10px] text-muted-foreground ml-auto">{filtered.length} entries</span>
      </div>
      <div className="space-y-1 max-h-96 overflow-y-auto">
        {filtered.slice(0, 50).map((log) => (
          <div key={log.log_id} className="flex items-start gap-2 text-xs py-1 border-b border-border/30 last:border-0">
            <span className="text-muted-foreground whitespace-nowrap flex-shrink-0 w-16">{new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            <span className={`font-medium flex-shrink-0 w-20 truncate ${log.outcome === 'failure' ? 'text-red-500' : log.outcome === 'escalated' ? 'text-yellow-500' : 'text-foreground'}`}>{log.action}</span>
            <span className="text-muted-foreground flex-1 min-w-0">{log.description}</span>
            <div className="flex items-center gap-2 flex-shrink-0">
              {log.model_name && <span className="text-muted-foreground/50 text-[10px]">{log.model_name.split('/').pop()}</span>}
              {log.cost_usd > 0 && <span className="text-muted-foreground/60 text-[10px]">${log.cost_usd.toFixed(3)}</span>}
              {log.tokens_used > 0 && <span className="text-muted-foreground/60 text-[10px]">{log.tokens_used.toLocaleString()} tok</span>}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-xs text-muted-foreground/50 text-center py-4">No matching logs</div>}
      </div>
    </div>
  );
};

// ── Conversation Panel ──────────────────────────────────────────────

const ConversationPanel: React.FC<{
  mission: Mission;
  onSend: (id: string, text: string) => Promise<void>;
}> = ({ mission, onSend }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const messages = mission.conversation || [];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await onSend(mission.mission_id, text.trim());
      setText('');
      inputRef.current?.focus();
    } finally {
      setSending(false);
    }
  };

  const lastMissionMsg = [...messages].reverse().find(m => m.role === 'mission');
  const isAwaitingReply = lastMissionMsg?.awaiting_reply;

  return (
    <div className="border-t border-border bg-muted/10 flex flex-col">
      <div className="flex items-center gap-1.5 px-4 pt-2 pb-1">
        <MessageSquare className="h-3 w-3 text-primary" />
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Mission Discussion</span>
        {messages.length > 0 && <span className="text-[10px] text-muted-foreground/50">({messages.length})</span>}
        {isAwaitingReply && (
          <span className="ml-auto text-[10px] font-medium text-amber-500 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />Waiting for your input
          </span>
        )}
      </div>
      <div ref={scrollRef} className="px-4 py-1.5 max-h-52 overflow-y-auto space-y-2">
        {messages.length === 0 && <p className="text-[11px] text-muted-foreground/60 italic py-2">No conversation yet. Send a message to discuss the mission.</p>}
        {messages.slice(-20).map((msg) => (
          <div key={msg.message_id} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
            <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed ${msg.role === 'user' ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-background border border-border rounded-bl-sm'}`}>
              <p className="whitespace-pre-wrap">{msg.text}</p>
            </div>
            <div className={`flex items-center gap-1.5 mt-0.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <span className="text-[9px] text-muted-foreground/50">{relativeTime(msg.timestamp)}</span>
              {msg.phase && <span className="text-[9px] text-muted-foreground/40">{msg.phase}</span>}
              {msg.action && <span className="text-[9px] px-1 py-0.5 rounded bg-muted text-muted-foreground/60">{msg.action}</span>}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 px-4 py-2.5">
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder={isAwaitingReply ? 'The mission is waiting for your reply...' : 'Message the mission...'}
          className={`flex-1 text-sm bg-background border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${isAwaitingReply ? 'border-amber-500/50' : 'border-border'}`}
        />
        <button onClick={handleSend} disabled={!text.trim() || sending} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors">
          {sending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Send
        </button>
      </div>
    </div>
  );
};

// ── Feedback Panel ──────────────────────────────────────────────────

const FeedbackPanel: React.FC<{
  mission: Mission;
  onSubmit: (id: string, rating: number, feedback: string) => Promise<void>;
}> = ({ mission, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const existingFeedback = mission.metadata?.feedback;

  if (existingFeedback) {
    return (
      <div className="border-t border-border bg-muted/10 px-4 py-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Your Review</span>
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map(s => (
              <span key={s} className={`text-sm ${s <= existingFeedback.rating ? 'text-amber-500' : 'text-muted-foreground/30'}`}>&#9733;</span>
            ))}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{existingFeedback.text}</p>
      </div>
    );
  }

  if (!['completed', 'failed', 'cancelled'].includes(mission.status)) return null;

  const handleSubmit = async () => {
    if (rating === 0 || !text.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(mission.mission_id, rating, text.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-t border-border bg-muted/10 px-4 py-3 space-y-2">
      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Rate this Mission</span>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(s => (
          <button key={s} onClick={() => setRating(s)} className={`text-lg transition-colors ${s <= rating ? 'text-amber-500' : 'text-muted-foreground/30 hover:text-amber-300'}`}>&#9733;</button>
        ))}
        {rating > 0 && <span className="text-xs text-muted-foreground ml-2">{rating}/5</span>}
      </div>
      <textarea value={text} onChange={e => setText(e.target.value)} placeholder="What went well? What could be improved?" className="w-full text-sm bg-background border border-border rounded-md px-3 py-1.5 h-16 resize-none focus:outline-none focus:ring-1 focus:ring-primary" />
      <button onClick={handleSubmit} disabled={rating === 0 || !text.trim() || submitting} className="text-xs font-medium px-4 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors">
        {submitting ? 'Submitting...' : 'Submit Feedback'}
      </button>
    </div>
  );
};

// ── Mission Detail View ─────────────────────────────────────────────

const MissionDetail: React.FC<{
  mission: Mission;
  logs: MissionLog[];
  standup: StandupReport | null;
  onBack: () => void;
  onAction: (id: string, action: string) => void;
  onInstruct: (id: string, text: string) => Promise<void>;
  onFeedback: (id: string, rating: number, feedback: string) => Promise<void>;
  onStepConfirmToggle: (id: string, enabled: boolean) => void;
}> = ({ mission, logs, standup, onBack, onAction, onInstruct, onFeedback, onStepConfirmToggle }) => {
  const [activeTab, setActiveTab] = useState<MissionDetailTab>('overview');
  const metadata = mission.metadata || {};

  const tabs: { id: MissionDetailTab; label: string; icon: React.ReactNode; show: boolean }[] = [
    { id: 'overview', label: 'Overview', icon: <Eye className="h-3 w-3" />, show: true },
    { id: 'plan', label: 'Plan', icon: <FileText className="h-3 w-3" />, show: !!mission.plan || getPhaseIndex(mission.phase) >= 1 },
    { id: 'execution', label: 'Execution', icon: <Zap className="h-3 w-3" />, show: getPhaseIndex(mission.phase) >= 2 || mission.artifacts.length > 0 },
    { id: 'discovery', label: 'Discovery', icon: <Eye className="h-3 w-3" />, show: !!metadata.discovery },
    { id: 'tests', label: 'Tests', icon: <TestTube className="h-3 w-3" />, show: getPhaseIndex(mission.phase) >= 3 || !!metadata.testing?.results?.length },
    { id: 'activity', label: 'Activity', icon: <Activity className="h-3 w-3" />, show: logs.length > 0 },
  ];
  const visibleTabs = tabs.filter(t => t.show);

  return (
    <div className="flex flex-col h-[80vh]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" />Back
          </button>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <Crosshair className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground truncate">{mission.title}</h3>
              <StatusBadge status={mission.status} />
              {mission.step_confirmation && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 border border-blue-500/20">Step-by-step</span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground truncate mt-0.5">{mission.objective}</p>
          </div>
        </div>
        <div className="flex-shrink-0 w-40">
          <PhaseTimeline mission={mission} />
        </div>
      </div>

      {/* Tab bar + controls */}
      <div className="flex items-center justify-between border-b border-border bg-muted px-4">
        <div className="flex items-center gap-0.5 overflow-x-auto">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
              }`}
            >
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => onStepConfirmToggle(mission.mission_id, !mission.step_confirmation)}
          className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded border transition-colors ${mission.step_confirmation ? 'bg-blue-500/10 text-blue-600 border-blue-500/20' : 'border-border text-muted-foreground hover:bg-muted/50'}`}
          title="Pause after each step for confirmation"
        >
          <Settings2 className="h-3 w-3" />Confirm steps
        </button>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
        {activeTab === 'overview' && <OverviewTab mission={mission} standup={standup} logs={logs} />}
        {activeTab === 'plan' && <PlanTab mission={mission} onAction={onAction} />}
        {activeTab === 'execution' && <ExecutionTab mission={mission} />}
        {activeTab === 'discovery' && <DiscoveryTab discovery={metadata.discovery} />}
        {activeTab === 'tests' && <TestsTab testing={metadata.testing} />}
        {activeTab === 'activity' && <ActivityTab logs={logs} />}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 px-4 py-2 border-t border-border bg-muted/5">
        {mission.status === 'proposed' && (
          <>
            <button onClick={() => onAction(mission.mission_id, 'approve')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-green-500/10 text-green-600 hover:bg-green-500/20 border border-green-500/20">
              <CheckCircle2 className="h-3 w-3" />Approve & Start
            </button>
            <button onClick={() => onAction(mission.mission_id, 'cancel')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-red-500/10 text-red-600 hover:bg-red-500/20 border border-red-500/20">
              <XCircle className="h-3 w-3" />Decline
            </button>
          </>
        )}
        {['discovery', 'planning', 'executing', 'testing', 'deployed'].includes(mission.status) && (
          <button onClick={() => onAction(mission.mission_id, 'pause')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20 border border-yellow-500/20">
            <Pause className="h-3 w-3" />Pause
          </button>
        )}
        {mission.status === 'paused' && (
          <button onClick={() => onAction(mission.mission_id, 'resume')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-green-500/10 text-green-600 hover:bg-green-500/20 border border-green-500/20">
            <Play className="h-3 w-3" />Resume
          </button>
        )}
        {!['completed', 'failed', 'cancelled'].includes(mission.status) && (
          <button onClick={() => onAction(mission.mission_id, 'cancel')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-red-500/10 text-red-600 hover:bg-red-500/20 border border-red-500/20 ml-auto">
            <XCircle className="h-3 w-3" />Cancel
          </button>
        )}
      </div>

      {/* Conversation + Feedback */}
      <ConversationPanel mission={mission} onSend={onInstruct} />
      <FeedbackPanel mission={mission} onSubmit={onFeedback} />
    </div>
  );
};

// ── Create Form ─────────────────────────────────────────────────────

const MissionCreateForm: React.FC<{ onCreated: () => void }> = ({ onCreated }) => {
  const [title, setTitle] = useState('');
  const [objective, setObjective] = useState('');
  const [costBudget, setCostBudget] = useState('5.00');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !objective.trim()) return;
    setSubmitting(true);
    try {
      const res = await missionsApi.create({
        title: title.trim(),
        objective: objective.trim(),
        costAllocatedUsd: parseFloat(costBudget) || 5.0,
      });
      if (res.success) {
        const missionId = res.data.mission_id;
        await missionsApi.approve(missionId);
        await missionsApi.start(missionId);
        setTitle('');
        setObjective('');
        setCostBudget('5.00');
        toast.success('Mission created and started');
        onCreated();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create mission');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 p-4 space-y-4">
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Title</label>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Mission title" className="w-full text-sm rounded-md border border-border px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30" required />
      </div>
      <div className="flex-1 flex flex-col">
        <label className="block text-xs font-medium text-muted-foreground mb-1">Objective</label>
        <textarea value={objective} onChange={e => setObjective(e.target.value)} placeholder="Describe what you want the agent to accomplish in detail..." className="w-full flex-1 min-h-[120px] text-sm rounded-md border border-border px-3 py-2 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" required />
      </div>
      <div className="flex items-center gap-3 pt-1">
        <div className="flex items-center gap-1.5">
          <label className="text-xs font-medium text-muted-foreground">Budget $</label>
          <input type="number" value={costBudget} onChange={e => setCostBudget(e.target.value)} step="0.50" min="0.50" className="w-20 text-sm rounded-md border border-border px-2.5 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30" />
        </div>
        <button type="submit" disabled={submitting || !title.trim() || !objective.trim()} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed ml-auto">
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}
          Create & Start
        </button>
      </div>
    </form>
  );
};

// ── Main Modal ──────────────────────────────────────────────────────

interface MissionModalProps extends UseMissionReturn {}

export function MissionModal({ isOpen, closeModal }: MissionModalProps) {
  const [missions, setMissions] = useState<Mission[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [missionLogs, setMissionLogs] = useState<Record<string, MissionLog[]>>({});
  const [standupReports, setStandupReports] = useState<Record<string, StandupReport>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'active' | 'completed' | 'all'>('active');
  const [showCreate, setShowCreate] = useState(false);

  const filterStatusParam = useCallback(() => {
    if (filter === 'active') return 'proposed,approved,discovery,planning,executing,testing,deployed,paused';
    if (filter === 'completed') return 'completed,failed,cancelled';
    return undefined;
  }, [filter]);

  const fetchMissions = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const res = await missionsApi.list(filterStatusParam());
      setMissions(res.data || []);
    } catch {
      toast.error('Failed to load missions');
    } finally {
      setLoading(false);
    }
  }, [filterStatusParam]);

  const fetchDetail = useCallback(async (id: string) => {
    try {
      const [logsRes, standupRes, detailRes] = await Promise.all([
        missionsApi.getLogs(id),
        missionsApi.getStandup(id).catch(() => null),
        missionsApi.get(id).catch(() => null),
      ]);
      setMissionLogs(prev => ({ ...prev, [id]: logsRes.data || [] }));
      if (standupRes?.data) setStandupReports(prev => ({ ...prev, [id]: standupRes.data }));
      if (detailRes?.data?.mission) {
        const detail = detailRes.data.mission;
        setMissions(prev => prev.map(m =>
          m.mission_id === id ? { ...m, metadata: detail.metadata, instructions: detail.instructions, conversation: detail.conversation, step_confirmation: detail.step_confirmation } : m
        ));
      }
    } catch { /* non-critical */ }
  }, []);

  const handleAction = useCallback(async (id: string, action: string) => {
    try {
      if (action === 'approve') {
        await missionsApi.approve(id);
        await missionsApi.start(id);
      } else if (action === 'pause') {
        await missionsApi.pause(id, 'Paused via editor');
      } else if (action === 'resume') {
        await missionsApi.resume(id);
      } else if (action === 'cancel') {
        await missionsApi.cancel(id);
      } else if (action === 'approve-plan') {
        await missionsApi.approvePlan(id);
      }
      await fetchMissions(false);
      if (selectedId === id) await fetchDetail(id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to ${action} mission`);
    }
  }, [fetchMissions, fetchDetail, selectedId]);

  const handleInstruct = useCallback(async (id: string, text: string) => {
    await missionsApi.instruct(id, text);
    await fetchDetail(id);
  }, [fetchDetail]);

  const handleFeedback = useCallback(async (id: string, rating: number, feedback: string) => {
    try {
      await missionsApi.feedback(id, rating, feedback);
      await fetchDetail(id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit feedback');
    }
  }, [fetchDetail]);

  const handleStepConfirmToggle = useCallback(async (id: string, enabled: boolean) => {
    try {
      await missionsApi.toggleStepConfirm(id, enabled);
      setMissions(prev => prev.map(m => m.mission_id === id ? { ...m, step_confirmation: enabled } : m));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to toggle step confirmation');
    }
  }, []);

  // Fetch on open + auto-refresh
  useEffect(() => {
    if (!isOpen) return;
    fetchMissions();
    setSelectedId(null);
    setShowCreate(false);
    const interval = setInterval(() => {
      fetchMissions(false);
      if (selectedId) fetchDetail(selectedId);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchDetail, fetchMissions, isOpen, selectedId]);

  // Fetch detail when selecting a mission
  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
  }, [selectedId, fetchDetail]);

  const selectedMission = missions.find(m => m.mission_id === selectedId);

  // Summary stats
  const activeCount = missions.filter(m => !['completed', 'failed', 'cancelled'].includes(m.status)).length;
  const totalSpend = missions.reduce((sum, m) => sum + m.budget.cost_used_usd, 0);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent variant="light" className="sm:max-w-4xl p-0 gap-0">
        {selectedMission ? (
          /* ── Detail View ── */
          <MissionDetail
            mission={selectedMission}
            logs={missionLogs[selectedMission.mission_id] || []}
            standup={standupReports[selectedMission.mission_id] || null}
            onBack={() => setSelectedId(null)}
            onAction={handleAction}
            onInstruct={handleInstruct}
            onFeedback={handleFeedback}
            onStepConfirmToggle={handleStepConfirmToggle}
          />
        ) : (
          /* ── List View ── */
          <>
            {/* Header */}
            <div className="relative pl-4 pr-4 pt-4 pb-2">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Crosshair className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Missions</h3>
                  <p className="text-[11px] text-muted-foreground">
                    {missions.length > 0
                      ? `${missions.length} mission${missions.length !== 1 ? 's' : ''} · ${activeCount} active · $${totalSpend.toFixed(2)} spent`
                      : 'Agent mission management'}
                  </p>
                </div>
              </div>
              {/* Positioned to align with the dialog close X button (absolute right-4 top-4) */}
              <button
                onClick={() => fetchMissions()}
                disabled={loading}
                className="absolute right-12 top-4 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-primary/20 bg-primary/10 text-xs font-medium text-primary transition-colors hover:bg-primary/15 hover:border-primary/30 disabled:opacity-50"
              >
                <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {/* Tabs */}
            <Tabs value={showCreate ? 'create' : 'missions'} onValueChange={v => setShowCreate(v === 'create')} className="flex flex-col">
              <div className="border-b border-border pl-4 pr-12 pt-2 pb-2 bg-muted">
                <TabsList className="grid w-full grid-cols-2 h-9 max-w-[280px]">
                  <TabsTrigger value="missions" className="flex items-center gap-1.5 text-xs">
                    <Layers className="h-3.5 w-3.5" />
                    Missions
                  </TabsTrigger>
                  <TabsTrigger value="create" className="flex items-center gap-1.5 text-xs">
                    <Plus className="h-3.5 w-3.5" />
                    Create
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Missions tab */}
              <TabsContent value="missions" className="data-[state=active]:h-[70vh] data-[state=inactive]:h-0 overflow-y-auto mt-0">
                {/* Filter toolbar */}
                <div className="px-4 py-3 flex items-center gap-2 border-b border-border">
                  <div className="flex gap-1">
                    {(['active', 'completed', 'all'] as const).map((f) => (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-3 py-1 text-xs rounded-md border ${filter === f ? 'bg-primary/10 text-primary border-primary/20' : 'border-border hover:bg-muted/50'}`}
                      >
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mission list */}
                <div className="px-4 py-3 space-y-2">
                  {loading && missions.length === 0 && (
                    <div className="flex items-center justify-center py-16 gap-3">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">Loading missions...</p>
                    </div>
                  )}
                  {!loading && missions.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                      <Crosshair className="h-10 w-10 text-muted-foreground/30" />
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">No missions found</p>
                        <p className="text-xs text-muted-foreground mt-1">Switch to the Create tab to start a new mission</p>
                      </div>
                    </div>
                  )}
                  {missions.map((mission) => (
                    <div
                      key={mission.mission_id}
                      className="flex items-center gap-3 px-4 py-3 bg-card border border-border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors"
                      onClick={() => setSelectedId(mission.mission_id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{mission.title}</span>
                          <StatusBadge status={mission.status} />
                          {mission.origin === 'agent_proposed' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-600 border border-purple-500/20">Agent</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{mission.objective}</p>
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-4">
                        <div className="w-32 hidden sm:block"><PhaseTimeline mission={mission} /></div>
                        <div className="w-28 hidden md:block"><BudgetBar budget={mission.budget} /></div>
                        <div className="text-xs text-muted-foreground whitespace-nowrap">{relativeTime(mission.updated_at)}</div>
                        {/* Quick actions */}
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          {['discovery', 'planning', 'executing', 'testing', 'deployed'].includes(mission.status) && (
                            <button onClick={() => handleAction(mission.mission_id, 'pause')} className="p-1 rounded hover:bg-yellow-500/10 text-yellow-600" title="Pause">
                              <Pause className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {mission.status === 'paused' && (
                            <button onClick={() => handleAction(mission.mission_id, 'resume')} className="p-1 rounded hover:bg-green-500/10 text-green-600" title="Resume">
                              <Play className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {mission.status === 'proposed' && (
                            <button onClick={() => handleAction(mission.mission_id, 'approve')} className="p-1 rounded hover:bg-green-500/10 text-green-600" title="Approve & Start">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Create tab */}
              <TabsContent value="create" className="data-[state=active]:h-[70vh] data-[state=inactive]:h-0 overflow-y-auto mt-0">
                <MissionCreateForm onCreated={() => { setShowCreate(false); fetchMissions(false); }} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
